import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImageSrc } from "@/lib/api";
import { S } from "./ibloom/styles";

/**
 * Public SEO landing page targeted at India's local shopkeepers / kirana owners.
 *
 * Why a dedicated route?
 *   • Google indexes app shells poorly. A static-feeling marketing page ranks
 *     for "free AI catalogue for shop", "list my dukaan online free", etc.
 *   • Acts as the "List your shop free" entry point we link from social ads.
 *
 * Composition (kept inline-styled to match the rest of the app's design tokens):
 *   Hero  →  3-step explainer  →  AI sample gallery (real DB images)
 *         →  Feature grid     →  Testimonial   →  FAQ   →  Final CTA
 */
export default function ShopkeepersLanding() {
  const [samples, setSamples] = useState([]);

  useEffect(() => {
    api.get("/mapbazzar/products").then((r) => {
      const items = (r.data || [])
        .filter((p) => p.image && (p.image.startsWith("/api/") || p.image.startsWith("http")))
        .slice(0, 6);
      setSamples(items);
    }).catch(() => setSamples([]));
  }, []);

  useEffect(() => {
    // Page-specific <title> so Google rich-results match the keyword target.
    const prev = document.title;
    document.title = "Get More Customers for Your Shop · MapBazzar — List Your Local Shop Online Free";
    return () => { document.title = prev; };
  }, []);

  return (
    <div style={{ background: "#FAF9F6", color: "#1A1A1A", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", minHeight: "100dvh" }}>
      <TopNav />

      {/* ===== HERO ===== */}
      <section style={{ position: "relative", overflow: "hidden", padding: "56px 22px 48px", background: "linear-gradient(180deg, #FFF9F0 0%, #FAF9F6 100%)" }}>
        <div aria-hidden style={{ position: "absolute", right: -60, top: -60, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(201,123,60,0.18), transparent 70%)" }} />
        <div aria-hidden style={{ position: "absolute", left: -80, bottom: -90, width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(15,61,46,0.12), transparent 70%)" }} />

        <div style={{ maxWidth: 1080, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr", gap: 30, position: "relative" }}>
          <div>
            <span style={{ display: "inline-block", padding: "5px 12px", borderRadius: 100, background: "#0F3D2E", color: "white", fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 14 }} data-testid="shopkeepers-eyebrow">
              🪷 100% Free for shopkeepers
            </span>
            <h1 style={{ ...S.fraunces, fontSize: "clamp(34px, 6vw, 56px)", fontWeight: 800, lineHeight: 1.05, letterSpacing: "-1.2px", color: "#0F3D2E", margin: "0 0 16px" }} data-testid="shopkeepers-h1">
              Get more customers<br />
              <span style={{ color: "#C97B3C" }}>and sales for your shop.</span>
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.55, color: "#3D2A0A", maxWidth: 640, margin: "0 0 26px" }}>
              MapBazzar brings local customers to your shop. List your products in one click (our <strong>AI writes the listings for you</strong>), we handle delivery and COD, and customers <strong>earn health insurance savings</strong> when they buy from you.
            </p>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
              <Link to="/app" data-testid="shopkeepers-hero-cta" style={primaryCta}>
                📸 List my shop free →
              </Link>
              <a href="#how-it-works" data-testid="shopkeepers-secondary-cta" style={secondaryCta}>
                See how it works
              </a>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 18, fontSize: 12.5, color: "#5C3D12" }}>
              <Badge>✓ No monthly fee</Badge>
              <Badge>✓ Works on any phone</Badge>
              <Badge>✓ WhatsApp customers directly</Badge>
            </div>
          </div>
        </div>
      </section>

      {/* ===== THE $BILLION UNLOCK ===== */}
      <section style={{ padding: "56px 22px", background: "linear-gradient(180deg, #0F3D2E 0%, #1C6B4A 100%)", color: "white" }} data-testid="shopkeepers-unlock-section">
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <span style={{ display: "inline-block", padding: "5px 12px", borderRadius: 100, background: "rgba(255,213,74,0.18)", color: "#FFE7A6", fontSize: 11, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 16 }}>
              ✨ The local-AI thesis
            </span>
            <h2 style={{ ...S.fraunces, fontSize: "clamp(28px, 4.6vw, 44px)", fontWeight: 800, lineHeight: 1.1, letterSpacing: "-1px", margin: "0 0 14px" }}>
              AI is unlocking <span style={{ color: "#FFD54A" }}>billions of dollars</span> of value<br />
              hidden inside every neighborhood shop.
            </h2>
            <p style={{ fontSize: 16.5, lineHeight: 1.65, opacity: 0.92, maxWidth: 760, margin: "0 auto" }}>
              For decades, the <strong>biggest inventory in India</strong> — sitting on the shelves of millions of kirana, saree, hardware, medical and stationery shops — was <strong>invisible to the internet</strong>. Not because shopkeepers didn't want to be online. But because <em>listing a product</em> meant typing, writing English/Hindi descriptions, editing photos, fighting with apps. <strong>Most shopkeepers don't do any of that.</strong>
            </p>
          </div>

          {/* Before / After AI */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 18, marginBottom: 36 }}>
            <div style={{ padding: 22, borderRadius: 18, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
              <div style={{ fontSize: 10.5, letterSpacing: 1.8, color: "#FFB4B4", fontWeight: 800, marginBottom: 10 }}>BEFORE AI · OFFLINE</div>
              <div style={{ ...S.fraunces, fontSize: 22, fontWeight: 700, marginBottom: 12 }}>Shelves full. Internet blank.</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 14, lineHeight: 1.7, opacity: 0.9 }}>
                <li>❌ Shopkeeper can't type fast or write listings in English</li>
                <li>❌ Photo editing, backgrounds, lighting — out of reach</li>
                <li>❌ Catalogue apps demand hours of data entry per SKU</li>
                <li>❌ Even loyal walk-in customers don't know what's in stock today</li>
              </ul>
            </div>
            <div style={{ padding: 22, borderRadius: 18, background: "linear-gradient(135deg, rgba(255,213,74,0.18), rgba(201,123,60,0.18))", border: "1px solid rgba(255,213,74,0.35)" }}>
              <div style={{ fontSize: 10.5, letterSpacing: 1.8, color: "#FFE7A6", fontWeight: 800, marginBottom: 10 }}>AFTER AI · ON MAPBAZAR</div>
              <div style={{ ...S.fraunces, fontSize: 22, fontWeight: 700, marginBottom: 12 }}>One click. Listing live.</div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 14, lineHeight: 1.7, opacity: 0.95 }}>
                <li>✅ <strong>ShopKeeper AI</strong> writes the title + description (Hindi/English) in seconds</li>
                <li>✅ <strong>AI lifestyle photo</strong> generated from a regular phone snap</li>
                <li>✅ Product auto-pinned on the map at the shopkeeper's exact location</li>
                <li>✅ <strong>Buyer AI agent</strong> can search inside the shop's live inventory & order</li>
              </ul>
            </div>
          </div>

          {/* Two AI agents */}
          <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 22, padding: 28 }}>
            <div style={{ textAlign: "center", marginBottom: 22 }}>
              <div style={{ fontSize: 10.5, letterSpacing: 1.8, color: "#FFE7A6", fontWeight: 800, marginBottom: 8 }}>HOW MAPBAZAR WORKS</div>
              <div style={{ ...S.fraunces, fontSize: 24, fontWeight: 700 }}>Two AI agents. One local economy.</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
              <div style={{ padding: 18, borderRadius: 16, background: "rgba(0,0,0,0.18)" }}>
                <div style={{ fontSize: 30, marginBottom: 10 }}>🧙‍♂️</div>
                <div style={{ ...S.fraunces, fontSize: 18, fontWeight: 700, marginBottom: 6 }}>ShopKeeper AI — AI agent for shopkeepers</div>
                <div style={{ fontSize: 13.5, lineHeight: 1.65, opacity: 0.9 }}>
                  Takes a single phone photo and, in <strong>under 30 seconds</strong>, produces a studio-quality lifestyle image, a Hindi/English listing, a fair price suggestion, and pins it to your shop on the map. No typing. No editing. No designer.
                </div>
              </div>
              <div style={{ padding: 18, borderRadius: 16, background: "rgba(0,0,0,0.18)" }}>
                <div style={{ fontSize: 30, marginBottom: 10 }}>🛵</div>
                <div style={{ ...S.fraunces, fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Mapbazar Buyer AI — for shoppers</div>
                <div style={{ fontSize: 13.5, lineHeight: 1.65, opacity: 0.9 }}>
                  Shoppers type what they need — <em>"red saree", "atta 5kg", "paracetamol"</em> — and the buyer AI searches <strong>inside live shop inventory</strong> across the neighborhood, ranks the nearest match, and brings it to your door in <strong>30 minutes</strong> (COD). They also earn HealthCoin on every purchase.
                </div>
              </div>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: 30 }}>
            <Link to="/app" data-testid="shopkeepers-unlock-cta" style={{ display: "inline-block", padding: "14px 26px", borderRadius: 100, background: "#FFD54A", color: "#0F3D2E", fontWeight: 800, fontSize: 14, textDecoration: "none", boxShadow: "0 10px 26px rgba(255,213,74,0.35)" }}>
              📸 Unlock your shop with AI →
            </Link>
            <div style={{ marginTop: 14, fontSize: 12, opacity: 0.75 }}>Free for shopkeepers · Built in India · 100% works on any phone</div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" style={{ padding: "48px 22px", background: "#FAF9F6" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <SectionTitle eyebrow="HOW IT WORKS" title="From a phone snap to a sale — in 3 taps." />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18, marginTop: 30 }}>
            <StepCard n={1} emoji="📸" title="Snap your product" body="Use any phone. Even an old Android. Lighting doesn't matter — ShopKeeper AI cleans it up." />
            <StepCard n={2} emoji="🧙‍♂️" title="ShopKeeper AI writes the listing" body="AI generates a studio-quality lifestyle image, Hindi/English title, description and fair price suggestion in ~20 seconds." />
            <StepCard n={3} emoji="📍" title="Customers find you" body="Your product appears on the Mapbazar map for shoppers nearby. They WhatsApp you in one tap." />
          </div>
        </div>
      </section>

      {/* ===== AI SAMPLE GALLERY ===== */}
      <section style={{ padding: "48px 22px", background: "linear-gradient(180deg, #FAF9F6 0%, #FFF9F0 100%)" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <SectionTitle eyebrow="✨ REAL AI LISTINGS" title="Made by shopkeepers like you." subtitle="Every photo below was generated by ShopKeeper AI from a regular phone snap. No designer, no studio, no cost." />
          {samples.length > 0 ? (
            <div data-testid="shopkeepers-sample-gallery" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 14, marginTop: 26 }}>
              {samples.map((p) => (
                <div key={p.id} style={{ background: "white", border: "1px solid #EAE8DC", borderRadius: 16, overflow: "hidden", boxShadow: "0 6px 18px rgba(0,0,0,0.05)" }}>
                  <div style={{ width: "100%", aspectRatio: "1/1", background: "#F0EFE8", overflow: "hidden" }}>
                    <img src={resolveImageSrc(p.image)} alt={p.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </div>
                  <div style={{ padding: "10px 12px" }}>
                    <div style={{ fontSize: 9.5, letterSpacing: 1.3, color: "#0F3D2E", fontWeight: 800, marginBottom: 4 }}>✨ AI · MUNIMJI</div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1A1A1A", lineHeight: 1.3, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{p.name}</div>
                    <div style={{ ...S.fraunces, fontSize: 15, fontWeight: 800, color: "#0F3D2E", marginTop: 4 }}>₹{p.price}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ marginTop: 26, padding: 26, border: "1px dashed #D5D3C7", borderRadius: 16, color: "#888", textAlign: "center", fontSize: 13 }}>
              Live samples loading…
            </div>
          )}
        </div>
      </section>

      {/* ===== FEATURE GRID ===== */}
      <section style={{ padding: "56px 22px", background: "#FAF9F6" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <SectionTitle eyebrow="WHY MAPBAZAR" title="Built for India's neighborhood shops." />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 30 }}>
            <FeatureCard icon="🖼️" title="Free AI lifestyle images" body="Studio-quality photos generated from any phone snap. No background needed." />
            <FeatureCard icon="✍️" title="AI ecommerce listing writer" body="Hindi + English titles, descriptions and price suggestions — written for you." />
            <FeatureCard icon="📍" title="Auto-pin on the local map" body="Your shop becomes discoverable to every Mapbazar shopper nearby." />
            <FeatureCard icon="💬" title="WhatsApp-first customers" body="No app installs for buyers. They tap your listing and message you directly." />
            <FeatureCard icon="🪙" title="HealthCoin loyalty engine" body="Customers earn rewards for shopping local — bringing them back to your shop." />
            <FeatureCard icon="🆓" title="Zero monthly fee" body="No commission, no subscription, no card needed. Use it forever, free." />
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIAL ===== */}
      <section style={{ padding: "48px 22px", background: "linear-gradient(135deg, #0F3D2E, #1C6B4A)", color: "white" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: 38, marginBottom: 16 }}>“</div>
          <div style={{ ...S.fraunces, fontSize: "clamp(20px, 3.5vw, 28px)", fontWeight: 600, lineHeight: 1.45, marginBottom: 22 }} data-testid="shopkeepers-testimonial">
            Pehle photo lo, fir laptop, fir kisi designer ko bolo… ab sirf ek click. ShopKeeper AI sab kuch bana deta hai aur grahak khud aate hain.
          </div>
          <div style={{ fontSize: 13, opacity: 0.85 }}>
            — Pinky Saree Bazaar, Nikol Ahmedabad · 8 listings in week 1
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section style={{ padding: "48px 22px", background: "#FAF9F6" }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <SectionTitle eyebrow="FAQ" title="Common questions" />
          <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 12 }}>
            <Faq q="Is this really free for shopkeepers?" a="Yes. Listings, AI photo generation, AI descriptions and map placement are all free — forever. We make money from optional add-ons (like premium ad placement) which you never need to use." />
            <Faq q="Do my customers need an app?" a="No. Customers open Mapbazar in their browser and message you on WhatsApp. No installs, no signup friction." />
            <Faq q="Will the AI describe my product correctly in Hindi?" a="Yes. ShopKeeper AI auto-detects your input language. Type in Hinglish, Hindi or English — it adapts the listing to match." />
            <Faq q="Can I set my exact shop location?" a="Yes. Open Profile → Your Shop and tap 'Set my shop location' — drop a pin or use GPS. Every product you list after that pins exactly there." />
            <Faq q="What if the AI photo doesn't look right?" a="One tap → Retry. Or check 'Use my photo as-is' and ShopKeeper AI will only write the listing, keeping your original photo." />
            <Faq q="Does Mapbazar take a commission on sales?" a="No commission on sales. You handle the transaction directly with the customer over WhatsApp or at your counter." />
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section style={{ padding: "60px 22px 80px", background: "linear-gradient(180deg, #FAF9F6 0%, #FDE7C8 100%)" }}>
        <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ ...S.fraunces, fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 800, color: "#0F3D2E", letterSpacing: "-0.6px", margin: "0 0 14px" }}>
            List your first product in under a minute.
          </h2>
          <p style={{ fontSize: 15.5, color: "#5C3D12", lineHeight: 1.55, margin: "0 0 28px" }}>
            Open Mapbazar, tap the 🧙‍♂️ ShopKeeper AI button on the map, upload one phone photo — your AI-powered listing is live for the whole neighborhood.
          </p>
          <Link to="/app" data-testid="shopkeepers-final-cta" style={{ ...primaryCta, padding: "16px 28px", fontSize: 16 }}>
            🚀 Start listing free
          </Link>
          <div style={{ marginTop: 22, fontSize: 12, color: "#8C5A1C" }}>
            No card · No subscription · Built in India for India
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ---------- Helpers ---------- */

function TopNav() {
  return (
    <div style={{ position: "sticky", top: 0, zIndex: 80, background: "rgba(250,249,246,0.92)", backdropFilter: "blur(18px)", borderBottom: "1px solid #EAE8DC" }}>
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link to="/" data-testid="shopkeepers-logo" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "#1A1A1A" }}>
          <img src="/icon-192.png" alt="Mapbazar" width={32} height={32} style={{ borderRadius: 9, display: "block" }} />
          <div>
            <div style={{ ...S.fraunces, fontSize: 19, fontWeight: 700, letterSpacing: "-0.5px", lineHeight: 1 }}>Mapbazar</div>
            <div style={{ fontSize: 9.5, letterSpacing: 1.5, color: "#0F3D2E", fontWeight: 600, marginTop: 2 }}>FOR SHOPKEEPERS</div>
          </div>
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Link to="/why-now" data-testid="shopkeepers-why-now-link" style={{ color: "#5C3D12", fontSize: 12.5, fontWeight: 700, textDecoration: "none", padding: "8px 4px" }}>
            Why Now?
          </Link>
          <Link to="/app" data-testid="shopkeepers-nav-cta" style={{ padding: "8px 16px", borderRadius: 100, background: "#0F3D2E", color: "white", fontSize: 12.5, fontWeight: 700, textDecoration: "none" }}>
            List free →
          </Link>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ eyebrow, title, subtitle }) {
  return (
    <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
      <div style={{ fontSize: 11, letterSpacing: 2, fontWeight: 800, color: "#C97B3C", marginBottom: 10 }}>{eyebrow}</div>
      <h2 style={{ ...S.fraunces, fontSize: "clamp(26px, 4.5vw, 38px)", fontWeight: 700, color: "#0F3D2E", letterSpacing: "-0.6px", lineHeight: 1.15, margin: 0 }}>{title}</h2>
      {subtitle && <p style={{ marginTop: 12, fontSize: 14.5, color: "#5C3D12", lineHeight: 1.6 }}>{subtitle}</p>}
    </div>
  );
}

function StepCard({ n, emoji, title, body }) {
  return (
    <div style={{ background: "white", border: "1px solid #EAE8DC", borderRadius: 18, padding: 22, position: "relative", boxShadow: "0 6px 22px rgba(0,0,0,0.04)" }}>
      <div style={{ position: "absolute", top: -14, left: 20, width: 32, height: 32, borderRadius: 10, background: "#0F3D2E", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, boxShadow: "0 4px 12px rgba(15,61,46,0.25)" }}>{n}</div>
      <div style={{ fontSize: 34, marginBottom: 10 }}>{emoji}</div>
      <div style={{ ...S.fraunces, fontSize: 20, fontWeight: 700, color: "#1A1A1A", marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13.5, color: "#555", lineHeight: 1.6 }}>{body}</div>
    </div>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <div style={{ background: "white", border: "1px solid #EAE8DC", borderRadius: 16, padding: 18 }}>
      <div style={{ width: 42, height: 42, borderRadius: 12, background: "linear-gradient(135deg, #FFF9F0, #FDE7C8)", border: "1px solid #F3CC8B", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 14.5, fontWeight: 700, color: "#1A1A1A", marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: "#666", lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="shopkeepers-faq-item" style={{ background: "white", border: "1px solid #EAE8DC", borderRadius: 14, overflow: "hidden" }}>
      <button onClick={() => setOpen((v) => !v)} style={{ width: "100%", background: "transparent", border: "none", padding: "16px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left", fontSize: 14, fontWeight: 700, color: "#1A1A1A" }}>
        <span>{q}</span>
        <span style={{ color: "#C97B3C", fontSize: 18, fontWeight: 700, flexShrink: 0 }}>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div style={{ padding: "0 18px 16px", fontSize: 13.5, color: "#555", lineHeight: 1.6 }}>{a}</div>
      )}
    </div>
  );
}

function Badge({ children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "5px 12px", borderRadius: 100, background: "white", border: "1px solid #F3CC8B", fontSize: 12, fontWeight: 600, color: "#5C3D12" }}>{children}</span>
  );
}

function Footer() {
  return (
    <footer style={{ borderTop: "1px solid #EAE8DC", padding: "26px 22px", background: "#FAF9F6", textAlign: "center", fontSize: 12, color: "#888" }}>
      <div style={{ marginBottom: 6 }}>
        <Link to="/app" style={{ color: "#0F3D2E", fontWeight: 700, textDecoration: "none" }}>Open Mapbazar</Link>
        <span style={{ margin: "0 10px" }}>·</span>
        <a href="https://wa.me/" style={{ color: "#0F3D2E", fontWeight: 700, textDecoration: "none" }} target="_blank" rel="noreferrer">Talk to us</a>
      </div>
      <div>© Mapbazar — built in India for India's local shopkeepers.</div>
    </footer>
  );
}

const primaryCta = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "14px 24px", borderRadius: 100,
  background: "#0F3D2E", color: "white",
  textDecoration: "none", fontWeight: 800, fontSize: 14.5,
  boxShadow: "0 10px 24px rgba(15,61,46,0.28)",
  border: "none", cursor: "pointer",
};

const secondaryCta = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "13px 22px", borderRadius: 100,
  background: "white", color: "#0F3D2E",
  textDecoration: "none", fontWeight: 700, fontSize: 14,
  border: "1.5px solid #0F3D2E",
};
